import { useState, useCallback } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { Upload, File, X, CheckCircle2, AlertCircle, Loader2, RefreshCcw, Info } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DOCUMENT_TYPES = [
  { value: 'medical_records', label: 'Medical Records' },
  { value: 'bills', label: 'Bills' },
  { value: 'imaging', label: 'Imaging/Diagnostics' },
  { value: 'other', label: 'Other' }
];

const STATUS_CONFIG = {
  pending: { label: 'Pending', icon: File, color: '#94A3B8' },
  uploading: { label: 'Uploading', icon: Loader2, color: '#3B82F6', animate: true },
  ocr_in_progress: { label: 'OCR in progress', icon: Loader2, color: '#F59E0B', animate: true },
  completed: { label: 'Completed', icon: CheckCircle2, color: '#22C55E' },
  failed: { label: 'Failed', icon: AlertCircle, color: '#EF4444' }
};

const UnifiedDocumentUploader = ({ caseId, onFilesUpdated }) => {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleFiles = useCallback((selectedFiles) => {
    const newFiles = selectedFiles.map(file => ({
      id: Math.random().toString(36).substr(2, 9),
      file,
      documentType: 'medical_records',
      status: 'pending',
      progress: 0
    }));
    setFiles(prev => [...prev, ...newFiles]);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFiles(Array.from(e.dataTransfer.files));
    }
  }, [handleFiles]);

  const handleFileInput = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFiles(Array.from(e.target.files));
    }
  };

  const updateFileDocumentType = (fileId, documentType) => {
    setFiles(prev => prev.map(f => 
      f.id === fileId ? { ...f, documentType } : f
    ));
  };

  const removeFile = (fileId) => {
    setFiles(prev => prev.filter(f => f.id !== fileId));
  };

  const uploadFile = async (fileItem) => {
    try {
      // Update status to uploading
      setFiles(prev => prev.map(f => 
        f.id === fileItem.id ? { ...f, status: 'uploading' } : f
      ));

      const formData = new FormData();
      formData.append('file', fileItem.file);
      formData.append('document_type', fileItem.documentType);

      const response = await axios.post(
        `${API}/cases/${caseId}/upload`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (progressEvent) => {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setFiles(prev => prev.map(f => 
              f.id === fileItem.id ? { ...f, progress } : f
            ));
          }
        }
      );

      const uploadedFileId = response.data.id;

      // Start OCR processing automatically
      setFiles(prev => prev.map(f => 
        f.id === fileItem.id ? { ...f, status: 'ocr_in_progress', uploadedFileId } : f
      ));

      const ocrResponse = await axios.post(
        `${API}/cases/${caseId}/files/${uploadedFileId}/process-ocr`
      );

      // Update status to completed
      setFiles(prev => prev.map(f => 
        f.id === fileItem.id ? {
          ...f,
          status: 'completed',
          ocrRequired: ocrResponse.data.ocr_required,
          textLength: ocrResponse.data.text_length
        } : f
      ));

      toast.success(`${fileItem.file.name} uploaded and processed`);
      if (onFilesUpdated) onFilesUpdated();

    } catch (error) {
      console.error('Upload error:', error);
      setFiles(prev => prev.map(f => 
        f.id === fileItem.id ? { ...f, status: 'failed' } : f
      ));
      toast.error(`Failed to upload ${fileItem.file.name}`);
    }
  };

  const handleUploadAll = async () => {
    setUploading(true);
    
    const pendingFiles = files.filter(f => f.status === 'pending');
    
    for (const file of pendingFiles) {
      await uploadFile(file);
    }
    
    setUploading(false);
    toast.success('All files uploaded successfully');
  };

  const retryUpload = async (fileItem) => {
    await uploadFile(fileItem);
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const StatusBadge = ({ status, ocrRequired }) => {
    const config = STATUS_CONFIG[status];
    const Icon = config.icon;
    
    return (
      <div className="flex items-center gap-2">
        <Icon 
          className={`h-4 w-4 ${config.animate ? 'animate-spin' : ''}`}
          style={{ color: config.color }}
        />
        <span className="text-sm font-medium" style={{ color: config.color }}>
          {config.label}
        </span>
        {status === 'completed' && ocrRequired && (
          <span className="text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: '#FEF3C7', color: '#854D0E' }}>
            OCR Applied
          </span>
        )}
      </div>
    );
  };

  return (
    <Card className="glass-card">
      <CardHeader>
        <CardTitle style={{ color: '#245451' }}>Upload Documents</CardTitle>
        <CardDescription style={{ color: '#64748B' }}>
          Upload medical records, bills, or imaging reports. OCR will be automatically applied to scanned files.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Important Notice */}
        <div 
          className="p-4 rounded-lg border"
          style={{ 
            backgroundColor: '#FFFFFF',
            borderColor: '#E5E5E5',
            borderWidth: '1px'
          }}
        >
          <div className="flex gap-3">
            <Info className="h-5 w-5 flex-shrink-0 mt-0.5" style={{ color: '#3B82F6' }} />
            <div className="flex-1">
              <p className="text-sm leading-relaxed" style={{ color: '#000000' }}>
                <span className="font-bold">Important:</span> For best processing accuracy, please upload medical records and billing documents <span className="font-bold">separately</span>. <span className="font-bold">Do NOT combine</span> reports and bills into a single PDF. Upload each file individually so the system can correctly detect records vs. bills.
              </p>
            </div>
          </div>
        </div>

        {/* Drag & Drop Zone */}
        <div
          className={`border-2 border-dashed rounded-lg p-8 text-center transition-all ${
            dragActive ? 'border-[#245451] bg-[#245451]/5' : 'border-[#E2E8F0]'
          }`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
        >
          <Upload className="h-12 w-12 mx-auto mb-4" style={{ color: '#84a2ac' }} />
          <p className="text-lg font-medium mb-2" style={{ color: '#245451' }}>
            Drag and drop files here
          </p>
          <p className="text-sm mb-4" style={{ color: '#64748B' }}>
            or click the button below to select files
          </p>
          <input
            type="file"
            id="file-input"
            multiple
            accept=".pdf,.docx,.doc,.jpg,.jpeg,.png,.tiff,.tif"
            onChange={handleFileInput}
            className="hidden"
          />
          <label htmlFor="file-input">
            <Button
              type="button"
              variant="outline"
              onClick={() => document.getElementById('file-input').click()}
              style={{ borderColor: '#245451', color: '#245451' }}
            >
              <Upload className="mr-2 h-4 w-4" />
              Select Files
            </Button>
          </label>
          <p className="text-xs mt-4" style={{ color: '#94A3B8' }}>
            Accepted formats: PDF, DOCX, JPEG, PNG, TIFF
          </p>
        </div>

        {/* File List */}
        {files.length > 0 && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold" style={{ color: '#245451' }}>
                Uploaded Files ({files.length})
              </h3>
              {files.some(f => f.status === 'pending') && (
                <Button
                  onClick={handleUploadAll}
                  disabled={uploading}
                  style={{ backgroundColor: '#245451' }}
                >
                  {uploading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload className="mr-2 h-4 w-4" />
                      Upload All
                    </>
                  )}
                </Button>
              )}
            </div>

            {files.map((fileItem) => (
              <div
                key={fileItem.id}
                className="p-4 rounded-lg border"
                style={{ borderColor: '#E2E8F0', backgroundColor: 'rgba(255, 255, 255, 0.5)' }}
              >
                <div className="flex items-start gap-4">
                  <File className="h-5 w-5 mt-1 flex-shrink-0" style={{ color: '#84a2ac' }} />
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-4 mb-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="font-medium truncate" style={{ color: '#245451' }}>
                            {fileItem.file.name}
                          </p>
                        </div>
                        <p className="text-sm" style={{ color: '#64748B' }}>
                          {formatFileSize(fileItem.file.size)}
                        </p>
                      </div>
                      
                      {fileItem.status === 'pending' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeFile(fileItem.id)}
                          className="h-8 w-8 p-0"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      )}
                    </div>

                    <div className="flex items-center gap-4 flex-wrap">
                      <div className="flex-1">
                        <Select
                          value={fileItem.documentType}
                          onValueChange={(value) => updateFileDocumentType(fileItem.id, value)}
                          disabled={fileItem.status !== 'pending'}
                        >
                          <SelectTrigger className="w-full">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {DOCUMENT_TYPES.map(type => (
                              <SelectItem key={type.value} value={type.value}>
                                {type.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="flex items-center gap-2">
                        <StatusBadge status={fileItem.status} ocrRequired={fileItem.ocrRequired} />
                        
                        {fileItem.status === 'failed' && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => retryUpload(fileItem)}
                            style={{ borderColor: '#EF4444', color: '#EF4444' }}
                          >
                            <RefreshCcw className="h-3 w-3 mr-1" />
                            Retry
                          </Button>
                        )}
                      </div>
                    </div>

                    {fileItem.status === 'uploading' && (
                      <div className="mt-2">
                        <div className="flex items-center justify-between text-xs mb-1">
                          <span style={{ color: '#3B82F6' }}>Uploading...</span>
                          <span style={{ color: '#64748B' }}>{fileItem.progress || 0}%</span>
                        </div>
                        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div
                            className="h-full transition-all duration-300"
                            style={{
                              width: `${fileItem.progress || 0}%`,
                              backgroundColor: '#3B82F6'
                            }}
                          />
                        </div>
                      </div>
                    )}
                    
                    {fileItem.status === 'ocr_in_progress' && (
                      <div className="mt-2">
                        <div className="flex items-center justify-between text-xs mb-1">
                          <span style={{ color: '#F59E0B' }}>Processing OCR...</span>
                          <span style={{ color: '#64748B' }}>Please wait</span>
                        </div>
                        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div
                            className="h-full animate-pulse"
                            style={{
                              width: '100%',
                              backgroundColor: '#F59E0B'
                            }}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default UnifiedDocumentUploader;
